import type { ReactNode } from "react";
import { X, type LucideIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface FilterChipProps {
  icon: LucideIcon;
  label: string;
  summary?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => void;
  children: ReactNode;
}

/**
 * A single active-filter chip: an icon + label (plus an optional value summary)
 * that opens its value dropdown when clicked, with an X to remove the filter.
 * @param icon - The filter type's icon
 * @param label - The filter type's label
 * @param summary - Optional summary of the selected value(s)
 * @param open - Whether the value dropdown is open
 * @param onOpenChange - Called when the dropdown open state changes
 * @param onRemove - Called when the chip's remove button is clicked
 * @param children - The value dropdown content
 * @returns The filter chip
 */
export function FilterChip({
  icon: Icon,
  label,
  summary,
  open,
  onOpenChange,
  onRemove,
  children,
}: FilterChipProps) {
  return (
    <div className="inline-flex items-center rounded-md border bg-background text-sm">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-l-md py-1 pl-2.5 pr-2 transition-colors hover:bg-muted"
          >
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{label}</span>
            {summary ? (
              <>
                <span className="text-muted-foreground">:</span>
                <span className="font-medium">{summary}</span>
              </>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          {children}
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={onRemove}
        className="flex items-center self-stretch rounded-r-md border-l px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
